import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productService, categoryService } from '../services/api';
import './ProductForm.css';
import { Typography, Row, Col, Card, Input, InputNumber, Select, Switch, Checkbox, Button, Alert, Upload, Space, Tag } from 'antd';
const { Title, Text } = Typography;
const { TextArea } = Input;

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    reference: '',
    description: '',
    shortDescription: '',
    categories: [],
    status: 'draft',
    isVisible: false,
    isFeatured: false,
    features: [],
    technicalSpecs: {},
    facets: {
      brand: '',
      system: '',
      gasType: '',
      detectorType: '',
      usage: '',
      connectivity: '',
      functionality: []
    },
    metaTitle: '',
    metaDescription: '',
    seoKeywords: []
  });

  const [categories, setCategories] = useState([]);
  const rootCategories = categories.filter(c => !c.parentCategory);
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState({
    datasheet: null,
    manual: null,
    certificate: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(isEdit);
  const [error, setError] = useState(null);

  // Helper pour AntD (set champ simple ou imbriqué)
  const setField = (name, value) => {
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };


  // Obtenir les champs spécifiques selon la catégorie sélectionnée
  const getCategorySpecificFields = (categoryId) => {
    if (!categoryId) return [];
    
    const category = categories.find(c => c._id === categoryId);
    if (!category || !category.specificFields) return [];

    // Convertir les specificFields de la catégorie en format pour le formulaire
    return Object.entries(category.specificFields).map(([fieldKey, fieldConfig]) => ({
      key: fieldKey,
      label: fieldConfig.label,
      type: fieldConfig.type,
      options: fieldConfig.options,
      required: fieldConfig.required
    }));
  };

  useEffect(() => {
    loadCategories();
    if (isEdit) {
      loadProduct();
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      const response = await categoryService.getCategories({ includeInactive: false });
      setCategories(response.categories);
      // Préselection si vide
      if (!selectedParent && response.categories) {
        const roots = response.categories.filter(c => !c.parentCategory);
        if (roots.length > 0) setSelectedParent(roots[0]._id);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    }
  };

  const loadProduct = async () => {
    try {
      setIsLoadingData(true);
      const response = await productService.getProductById(id);
      const product = response.product;
      
      setFormData({
        name: product.name || '',
        reference: product.reference || '',
        description: product.description || '',
        shortDescription: product.shortDescription || '',
        categories: product.categories?.map(cat => cat._id) || [],
        status: product.status || 'draft',
        isVisible: product.isVisible || false,
        isFeatured: product.isFeatured || false,
        features: product.features || [],
        technicalSpecs: product.technicalSpecs || {},
        facets: product.facets || {
          brand: '',
          system: '',
          gasType: '',
          detectorType: '',
          usage: '',
          connectivity: '',
          functionality: []
        },
        metaTitle: product.metaTitle || '',
        metaDescription: product.metaDescription || '',
        seoKeywords: product.seoKeywords || []
      });
      // Déterminer le parent sélectionné à partir des catégories du produit
      if (product.categories && product.categories.length > 0) {
        const parent = categories.find(c => product.categories.some(pc => String(pc._id || pc) === String(c._id)) && !c.parentCategory);
        if (parent) setSelectedParent(parent._id);
      }

      // Charger les images existantes
      if (product.images) {
        const existingImages = [];
        if (product.images.main) {
          existingImages.push({
            url: product.images.main,
            isMain: true,
            isExisting: true
          });
        }
        if (product.images.gallery) {
          product.images.gallery.forEach(img => {
            existingImages.push({
              url: img,
              isMain: false,
              isExisting: true
            });
          });
        }
        setImages(existingImages);
      }

      // Charger les documents existants
      if (product.documents) {
        setDocuments({
          datasheet: product.documents.datasheet ? { url: product.documents.datasheet, isExisting: true } : null,
          manual: product.documents.manual ? { url: product.documents.manual, isExisting: true } : null,
          certificate: product.documents.certificate ? { url: product.documents.certificate, isExisting: true } : null
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
      setError('Erreur lors du chargement du produit');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleArrayInputChange = (field, value) => {
    const items = value.split('\n').filter(item => item.trim());
    setFormData(prev => ({
      ...prev,
      [field]: items
    }));
  };

  const handleCategoryChange = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId]
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      isMain: false,
      isExisting: false
    }));
    
    setImages(prev => [...prev, ...newImages]);
  };

  const handleDocumentUpload = (type, file) => {
    setDocuments(prev => ({
      ...prev,
      [type]: { file, isExisting: false }
    }));
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const setMainImage = (index) => {
    setImages(prev => prev.map((img, i) => ({
      ...img,
      isMain: i === index
    })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Préparer les données
      const submitData = { ...formData };
      
      // Ajouter les nouvelles images
      const newImages = images.filter(img => !img.isExisting);
      if (newImages.length > 0) {
        submitData.images = newImages.map(img => img.file);
      }

      // Ajouter les nouveaux documents
      const newDocuments = {};
      Object.keys(documents).forEach(key => {
        if (documents[key] && !documents[key].isExisting) {
          newDocuments[key] = documents[key].file;
        }
      });
      if (Object.keys(newDocuments).length > 0) {
        submitData.documents = newDocuments;
      }

      // Debug: Afficher les données envoyées
      console.log('Données envoyées:', submitData);
      console.log('Images:', submitData.images);
      console.log('Documents:', submitData.documents);

      if (isEdit) {
        await productService.updateProduct(id, submitData);
      } else {
        await productService.createProduct(submitData);
      }

      navigate('/admin/products');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      // Afficher plus de détails sur l'erreur
      let errorMessage = 'Erreur lors de la sauvegarde du produit';
      if (error.response && error.response.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        if (error.response.data.errors) {
          errorMessage += ': ' + error.response.data.errors.map(e => e.msg).join(', ');
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="product-form-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement du produit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="product-form-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="page-title">
          <Title level={3} style={{ margin: 0 }}>{isEdit ? 'Modifier le produit' : 'Nouveau produit'}</Title>
          <Text type="secondary">{isEdit ? 'Modifiez les informations du produit' : 'Créez un nouveau produit'}</Text>
        </div>
        <Space>
          <Button onClick={() => navigate('/admin/products')}>Retour</Button>
          <Button type="primary" loading={isLoading} onClick={handleSubmit}>{isEdit ? 'Mettre à jour' : 'Créer le produit'}</Button>
        </Space>
      </div>

      {error && (
        <Alert type="error" showIcon message="Erreur" description={error} style={{ marginBottom: 16 }} />
      )}

      <form onSubmit={handleSubmit} className="product-form">
        <div className="form-grid">
          {/* Informations générales */}
          <Card className="form-section" title="Informations générales">
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Text strong>Nom du produit *</Text>
                <Input value={formData.name} onChange={(e) => setField('name', e.target.value)} placeholder="Nom du produit" required />
              </Col>
              <Col xs={24} md={12}>
                <Text strong>Référence *</Text>
                <Input value={formData.reference} onChange={(e) => setField('reference', e.target.value)} placeholder="Référence du produit" style={{ textTransform: 'uppercase' }} required />
              </Col>
              <Col span={24}>
                <Text strong>Description courte</Text>
                <TextArea rows={3} value={formData.shortDescription} onChange={(e) => setField('shortDescription', e.target.value)} maxLength={300} placeholder="Description courte (300 caractères max)" />
              </Col>
              <Col span={24}>
                <Text strong>Description détaillée *</Text>
                <TextArea rows={6} value={formData.description} onChange={(e) => setField('description', e.target.value)} required placeholder="Description détaillée du produit" />
              </Col>
            </Row>
          </Card>

          {/* Champs spécifiques à la catégorie */}
          <Card className="form-section" title="Caractéristiques spécifiques">
            <Row gutter={[12, 12]}>
              {/* Sélection de la catégorie */}
              <Col xs={24}>
                <Text strong>Catégorie *</Text>
                <Select
                  value={formData.categories && formData.categories.length > 0 ? formData.categories[0] : null}
                  onChange={(val) => {
                    setField('categories', val ? [val] : []);
                    // Reset specific fields when category changes
                    const specificFields = getCategorySpecificFields(val);
                    specificFields.forEach(field => {
                      if (field.type === 'multiselect') {
                        setField(`facets.${field.key}`, []);
                      } else {
                        setField(`facets.${field.key}`, '');
                      }
                    });
                  }}
                  style={{ width: '100%' }}
                  options={rootCategories.map(c => ({ value: c._id, label: c.name }))}
                  placeholder="Sélectionner une catégorie"
                />
              </Col>

              {/* Champs spécifiques dynamiques */}
              {formData.categories && formData.categories.length > 0 && getCategorySpecificFields(formData.categories[0]).map((field) => (
                  <Col xs={24} md={12} key={field.key}>
                    <Text strong>{field.label}</Text>
                    {field.type === 'select' ? (
                      <Select
                        value={formData.facets[field.key]}
                        onChange={(value) => setField(`facets.${field.key}`, value)}
                        style={{ width: '100%' }}
                        options={field.options.map(opt => ({ value: opt, label: opt }))}
                        placeholder={`Sélectionner ${field.label.toLowerCase()}`}
                        allowClear
                      />
                    ) : field.type === 'multiselect' ? (
                      <Select
                        mode="multiple"
                        value={formData.facets[field.key]}
                        onChange={(values) => setField(`facets.${field.key}`, values)}
                        style={{ width: '100%' }}
                        options={field.options.map(opt => ({ value: opt, label: opt }))}
                        placeholder={`Sélectionner ${field.label.toLowerCase()}`}
                        allowClear
                      />
                    ) : (
                      <Input
                        value={formData.facets[field.key]}
                        onChange={(e) => setField(`facets.${field.key}`, e.target.value)}
                        placeholder={`Saisir ${field.label.toLowerCase()}`}
                      />
                    )}
                  </Col>
                ))}
            </Row>
          </Card>

          {/* Images et documents */}
          <Card className="form-section" title="Images et documents">
            <div className="form-group">
              <label className="form-label">Images</label>
              <Upload.Dragger multiple accept="image/*" beforeUpload={() => false} onChange={(info) => {
                const files = (info.fileList || []).map(f => f.originFileObj).filter(Boolean);
                const newImages = files.map(file => ({ file, isMain: false, isExisting: false }));
                setImages(prev => [...prev, ...newImages]);
              }}>
                <p className="ant-upload-drag-icon"><i className="fas fa-cloud-upload-alt"></i></p>
                <p>Glissez-déposez des images ou cliquez pour sélectionner</p>
              </Upload.Dragger>
              <div className="images-preview">
                {images.map((image, index) => (
                  <div key={index} className={`image-item ${image.isMain ? 'main' : ''}`}>
                    {image.isExisting ? (
                      <img src={`http://localhost:5000${image.url}`} alt="Produit" />
                    ) : (
                      <img src={URL.createObjectURL(image.file)} alt="Produit" />
                    )}
                    <div className="image-actions">
                      {!image.isMain && (
                        <button
                          type="button"
                          className="btn btn--sm btn--primary"
                          onClick={() => setMainImage(index)}
                        >
                          <i className="fas fa-star"></i>
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        onClick={() => removeImage(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                    {image.isMain && (
                      <div className="main-badge">Principale</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Fiche technique (PDF)</label>
              <Upload beforeUpload={() => false} accept=".pdf" maxCount={1} onChange={(info) => {
                const f = info.fileList?.[0]?.originFileObj;
                if (f) handleDocumentUpload('datasheet', f);
              }}>
                <Button>Choisir un fichier</Button>
              </Upload>
              {documents.datasheet && (
                <div className="document-preview">
                  <i className="fas fa-file-pdf"></i>
                  <span>
                    {documents.datasheet.isExisting 
                      ? 'Fichier existant' 
                      : documents.datasheet.file.name
                    }
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Manuel d'utilisation (PDF)</label>
              <Upload beforeUpload={() => false} accept=".pdf" maxCount={1} onChange={(info) => {
                const f = info.fileList?.[0]?.originFileObj;
                if (f) handleDocumentUpload('manual', f);
              }}>
                <Button>Choisir un fichier</Button>
              </Upload>
              {documents.manual && (
                <div className="document-preview">
                  <i className="fas fa-file-pdf"></i>
                  <span>
                    {documents.manual.isExisting 
                      ? 'Fichier existant' 
                      : documents.manual.file.name
                    }
                  </span>
                </div>
              )}
            </div>
          </Card>

          {/* Statut et visibilité */}
          <Card className="form-section" title="Statut et visibilité">
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <Text strong>Statut</Text>
                <Select
                  value={formData.status}
                  onChange={(v) => setField('status', v)}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'draft', label: 'Brouillon' },
                    { value: 'published', label: 'Publié' },
                    { value: 'archived', label: 'Archivé' }
                  ]}
                />
              </Col>
              <Col xs={24} md={8}>
                <Text strong>Visible sur le site</Text>
                <div><Switch checked={formData.isVisible} onChange={(checked) => setField('isVisible', checked)} /></div>
              </Col>
              <Col xs={24} md={8}>
                <Text strong>Produit mis en avant</Text>
                <div><Switch checked={formData.isFeatured} onChange={(checked) => setField('isFeatured', checked)} /></div>
              </Col>
            </Row>
          </Card>

          {/* Caractéristiques */}
          <Card className="form-section" title="Caractéristiques">
            <Text strong>Caractéristiques principales</Text>
            <TextArea rows={4} value={formData.features.join('\n')} onChange={(e) => handleArrayInputChange('features', e.target.value)} placeholder="Une caractéristique par ligne" />
          </Card>
        </div>

        <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={() => navigate('/admin/products')}>Annuler</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>{isEdit ? 'Mettre à jour' : 'Créer le produit'}</Button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
